import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import loadScript from 'lib/loadScript';

/**
 * FacebookEmbed renders a Facebook post or video embed based on the provided widget properties.
 *
 * @param {object} props - The component props.
 * @param {string} [props.className=''] - Additional class names for the embed container.
 * @param {object} props.presentation - Presentation settings, including size.
 * @param {object} props.widget - Widget data containing Facebook post or video URLs.
 * @param {string} [props.facebookPostType='post'] - Type of Facebook embed ('post' or 'video').
 * @returns {JSX.Element} The Facebook embed element
 */
export function FacebookEmbed({
  className = '',
  presentation,
  widget,
  facebookPostType = 'post',
}) {
  const containerDiv = useRef(null);

  /**
   * Returns the Facebook post or video URL from widget properties.
   * @returns {string} The URL of the Facebook post or video.
   */
  const getPostUrl = () => widget.properties['facebookplus-post-url'] || widget.properties['video-url'];

  useEffect(() => {
    loadScript('https://connect.facebook.net/en_US/sdk.js#xfbml=false&version=v2.10')
      // re-initing here since in SPA mode, the script needs to re-parse
      // embeds on the page when navigating to a new article
      .thenOnFirstLoad(() => window.FB.init({ version: 'v2.10', xfbml: true }))
      .then(() => window.FB.XFBML.parse());
  }, []);

  /**
   * Renders the Facebook video embed container.
   * @returns {JSX.Element} The Facebook video embed element.
   */
  const wrapped = () => {
    const { size } = presentation;
    return (
      <div
        className="fb-video"
        data-href={getPostUrl()}
        data-show-text="true"
        data-testid="facebook-embed-video"
        width={size.toLowerCase() === 'small' ? 360 : null}
        ref={containerDiv}
      />
    );
  };

  if (
    facebookPostType === 'video'
    || widget.properties['facebookplus-post-url']?.includes('watch')
    || widget.properties['facebookplus-post-url']?.includes('video')
  ) {
    return wrapped();
  }

  const classes = classNames({
    'embed-widget__use-presentation': true,
    [className]: className,
    'fb-post': facebookPostType === 'post',
  });

  return (
    <div
      className={classes}
      data-href={getPostUrl()}
      data-show-text="true"
      data-width="auto"
      ref={containerDiv}
      data-testid="facebook-embed"
    />
  );
}
